import { Injectable } from '@angular/core';
import moment from 'moment';
import { isJsonUserModel, JsonUserModel, UserModel, UserModelFromJson } from '../../models/complex/user.model';
import { LocalStorageCommonService } from './local-storage-common.service';
import { from, map, Observable } from 'rxjs';
import { EnvironmentService } from '@evorait/pace-environment';

const KEY_IS_INITIAL_START = 'isInitialStart';
const KEY_APP_UPGRADE_VERSION = 'AppUpgradeVersion';
const KEY_SALES_PLANT = 'SalesAndPlant';
const KEY_BOOT_STOCK_LAST_SYNC = 'bootStockLastSyncTime';

@Injectable({
    providedIn: 'root',
})
export class LocalStorageAppStateService {
    public constructor(
        private readonly environmentService: EnvironmentService,
        private readonly localStorageCommonService: LocalStorageCommonService,
    ) {}

    public async getInitialStartFlagAsync(): Promise<boolean> {
        // if a value is there, it cannot be the first start. So only return 'true' if no value is returned.
        return this.localStorageCommonService
            .storageGetAsync(KEY_IS_INITIAL_START)
            .then((isInitialStart) => typeof isInitialStart === 'undefined');
    }

    public setInitialStartFlag(isInitialStart: boolean): Observable<void> {
        return from(this.localStorageCommonService.storageSetAsync(KEY_IS_INITIAL_START, isInitialStart));
    }

    public getUserRole(): Observable<string> {
        return this.getUserData().pipe(
            map((userData) => {
                if (userData && userData?.getUserRole()) {
                    return userData.getUserRole();
                }
                return '';
            }),
        );
    }

    public getUserData(): Observable<UserModel | undefined> {
        return from(this.localStorageCommonService.storageGetAsync(KEY_SALES_PLANT)).pipe(
            map((userData) => {
                // TODO: Migration logic. Remove once the next version after 2.72.0 goes live.;
                if (
                    typeof userData === 'object' &&
                    userData !== null &&
                    !(userData as Record<string, unknown>).hasOwnProperty('appId')
                ) {
                    (userData as JsonUserModel).appId = this.environmentService.appId;
                }

                if (isJsonUserModel(userData)) {
                    return new UserModelFromJson(userData);
                }
                return undefined;
            }),
        );
    }

    public async setUserData(selectedUserModel: UserModel): Promise<void> {
        return this.localStorageCommonService.storageSetAsync(KEY_SALES_PLANT, selectedUserModel);
    }

    /**
     * Return the version number of the last successful offline store upgrade.
     */
    public async getLastUpgradeVersionAsync(): Promise<string> {
        return this.localStorageCommonService
            .storageGetAsync<string>(KEY_APP_UPGRADE_VERSION)
            .then((lastUpgradeVersion) => {
                if (typeof lastUpgradeVersion === 'string') {
                    return lastUpgradeVersion;
                }
                return '';
            });
    }

    /**
     * Set the version number of the last successful offline store upgrade.
     *
     * @param version
     */
    public async setLastUpgradeVersion(version: string): Promise<void> {
        return this.localStorageCommonService.storageSetAsync(KEY_APP_UPGRADE_VERSION, version);
    }

    public async saveLastSyncBootStockTime(lastSyncBootStockTime: moment.Moment): Promise<void> {
        return this.localStorageCommonService.storageSetAsync(
            KEY_BOOT_STOCK_LAST_SYNC,
            lastSyncBootStockTime.toISOString(),
        );
    }

    public async getLastSyncBootStockTimeAsync(): Promise<moment.Moment> {
        return this.localStorageCommonService
            .storageGetAsync<string | undefined>(KEY_BOOT_STOCK_LAST_SYNC)
            .then((lastSyncBootStockTime) => {
                if (typeof lastSyncBootStockTime === 'string') {
                    return moment(lastSyncBootStockTime);
                }
                return moment.invalid();
            });
    }
}
